import React, { useContext } from 'react';
import { theme as antTheme } from 'antd';

import DeviceEndOfSupportContext from '@totem/components/deviceEndOfSupport/deviceEndOfSupportContext';
import { EndOfSupportStatus } from '@totem/components/deviceEndOfSupport/types';
import { STATUS } from '@totem/components/deviceEndOfSupport/utilities';

import './deviceEndOfSupport.css';

type Tile = {
  label: string;
  value: number;
  detail: string;
  tone?: 'error' | 'warning';
  // status filter the tile applies; empty = show all
  statuses: EndOfSupportStatus[];
};

const KpiTiles = () => {
  const { rows, coverage, filters, setFilter } = useContext(
    DeviceEndOfSupportContext,
  );
  const { token } = antTheme.useToken();

  const count = (status: EndOfSupportStatus) =>
    rows.filter((row) => row.status === status).length;
  const managed = rows.filter((row) => row.managed).length;
  const estate = coverage
    ? coverage.withRecord.managed +
      coverage.withRecord.notManaged +
      coverage.withoutRecord.managed +
      coverage.withoutRecord.notManaged
    : null;

  const tiles: Tile[] = [
    {
      label: 'Devices with a lifecycle record',
      value: rows.length,
      detail:
        estate === null
          ? `${managed} under IB management`
          : `of ${estate.toLocaleString('en-US')} devices · ${managed} under IB management`,
      statuses: [],
    },
    {
      label: 'Past end of service',
      value: count('past'),
      detail: 'End of Support date has passed',
      tone: 'error',
      statuses: ['past'],
    },
    {
      label: 'Ending within 12 months',
      value: count('soon'),
      detail: 'plan replacement',
      tone: 'warning',
      statuses: ['soon'],
    },
    {
      label: 'In service',
      value: count('ok'),
      detail: 'more than 12 months left',
      statuses: ['ok'],
    },
    {
      label: 'No usable date',
      value: count('none') + count('check'),
      detail: 'missing, or does not fit the OS',
      tone: 'warning',
      statuses: ['none', 'check'],
    },
  ];

  const selected = filters.status ?? [];
  const isActive = (tile: Tile) =>
    tile.statuses.length > 0 &&
    selected.length === tile.statuses.length &&
    tile.statuses.every((status) => selected.includes(STATUS[status].label));

  const toneColor = (tile: Tile) => {
    if (tile.value === 0 || !tile.tone) {
      return token.colorText;
    }
    return tile.tone === 'error' ? token.colorError : token.colorWarning;
  };

  return (
    <div styleName="kpis">
      {tiles.map((tile) => {
        const active = isActive(tile);
        return (
          <button
            key={tile.label}
            type="button"
            styleName="kpi"
            aria-pressed={active}
            aria-label={`${tile.statuses.length ? `Show only: ${tile.label}` : 'Show all devices'} (${tile.value})`}
            onClick={() =>
              setFilter(
                'status',
                active || tile.statuses.length === 0
                  ? []
                  : tile.statuses.map((status) => STATUS[status].label),
              )
            }
            style={{
              background: token.colorBgContainer,
              borderColor: active ? token.colorPrimary : token.colorBorder,
              boxShadow: active
                ? `inset 0 0 0 1px ${token.colorPrimary}`
                : undefined,
              borderRadius: token.borderRadiusLG,
            }}
          >
            {tile.statuses.length > 0 && (
              <span
                styleName="kpi-chevron"
                aria-hidden="true"
                style={{
                  color: active
                    ? token.colorPrimary
                    : token.colorTextQuaternary,
                }}
              >
                {active ? '✕' : '›'}
              </span>
            )}
            <div
              styleName="kpi-label"
              style={{ color: token.colorTextSecondary }}
            >
              {tile.label}
            </div>
            <div styleName="kpi-value" style={{ color: toneColor(tile) }}>
              {tile.value}
            </div>
            <div
              styleName="kpi-detail"
              style={{ color: token.colorTextSecondary }}
            >
              {tile.detail}
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default KpiTiles;
